import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import CardFlagIcon from './CardFlagIcon';
import { getContrastColor } from '../../utils/planningColors';
import { formatDateFr } from '../../utils/planningTime';
import './RechercheDossier.css';

// ═══════════════════════════════════════════════════════════════════════════
// Recherche d'un dossier dans TOUT le planning.
//
// L'ancienne barre ne cherchait que dans la journée affichée : elle estompait
// les cartes qui ne correspondaient pas. Autrement dit, il fallait déjà savoir
// quel jour regarder — c'est-à-dire connaître la réponse à la question qu'on
// se pose. Ici la recherche ignore la date affichée ; c'est le résultat qui
// dit quel jour c'était.
//
// Les résultats reprennent le vocabulaire des cartes de la grille (couleur du
// statut, pictos, immat, n° d'OR) pour qu'on reconnaisse le dossier, et y
// ajoutent ce que la grille ne montre pas et qu'on cherche justement : la
// DATE, l'heure et le mécanicien.
// ═══════════════════════════════════════════════════════════════════════════

const MINI = 2;          // en deçà, on ne lance rien : « a » ramènerait tout
const DEBOUNCE_MS = 300;
const PLAFOND = 200;     // doit rester aligné sur le LIMIT du backend

// La migration Mécaplanning (août-septembre 2026) importait jour par jour : un
// rendez-vous étalé sur plusieurs jours a donné UN DOSSIER CRM PAR JOUR, tous
// marqués « [API Mecaplanning #RdvId] » dans leur commentaire. La recherche les
// affichait comme autant de dossiers différents. On les regroupe à l'affichage
// en une ligne (« 3 jours ») ; la ligne ouvre le dossier qui porte un statut
// s'il y en a un, sinon le plus récent. Les données ne sont pas touchées ici.
const MARQUEUR_MECA = /^\[API Mecaplanning #(\d+)\]/;
const regrouperJoursMecaplanning = (liste) => {
  const groupes = new Map();
  const sortie = [];
  for (const i of liste) {
    const m = MARQUEUR_MECA.exec(i.commentaire || '');
    if (!m) { sortie.push(i); continue; }
    const cle = `${m[1]}|${i.immat || ''}`;
    if (!groupes.has(cle)) { const g = { ...i, _jours: [] }; groupes.set(cle, g); sortie.push(g); }
    groupes.get(cle)._jours.push(i);
  }
  return sortie.map(g => {
    if (!g._jours || g._jours.length < 2) return g._jours ? g._jours[0] : g;
    const jours = g._jours;
    const repr = jours.find(j => j.statut_atelier_id) || jours[0];
    const dates = jours.map(j => j.date).filter(Boolean).sort();
    return { ...repr, _nbJours: jours.length, _du: dates[0], _au: dates[dates.length - 1] };
  });
};

export default function RechercheDossier({ show, onClose, onOuvrir }) {
  const [texte, setTexte] = useState('');
  const [resultats, setResultats] = useState([]);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState('');
  const [actif, setActif] = useState(0);

  const champRef = useRef(null);
  const listeRef = useRef(null);
  const abortRef = useRef(null);
  const timerRef = useRef(null);

  // Réinitialisation à chaque ouverture : rouvrir la loupe sur les résultats
  // de la recherche précédente ferait douter de ce qu'on regarde.
  useEffect(() => {
    if (!show) return;
    setTexte(''); setResultats([]); setErreur(''); setActif(0);
    const t = setTimeout(() => champRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, [show]);

  const lancer = useCallback((q) => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setChargement(true);
    // Pas de date_from/date_to : c'est ce qui rend la recherche globale.
    api.get('/planning/interventions', {
      params: { search: q, inclure_a_planifier: '1' },
      signal: ctrl.signal,
    })
      .then(r => { setResultats(regrouperJoursMecaplanning(r.data?.interventions || [])); setErreur(''); setActif(0); })
      .catch(e => { if (e.name !== 'CanceledError' && e.code !== 'ERR_CANCELED') setErreur('La recherche a échoué.'); })
      .finally(() => { if (abortRef.current === ctrl) setChargement(false); });
  }, []);

  useEffect(() => {
    clearTimeout(timerRef.current);
    const q = texte.trim();
    if (q.length < MINI) { abortRef.current?.abort(); setResultats([]); setChargement(false); return; }
    timerRef.current = setTimeout(() => lancer(q), DEBOUNCE_MS);
    return () => clearTimeout(timerRef.current);
  }, [texte, lancer]);

  // Fermeture et navigation au clavier — un comptoir tape plus vite qu'il ne vise.
  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
    if (resultats.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActif(i => Math.min(i + 1, resultats.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActif(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); ouvrir(resultats[actif]); }
  };

  // Garde l'élément sélectionné au clavier dans le champ de vision.
  useEffect(() => {
    const el = listeRef.current?.querySelector('.rdo-carte.is-active');
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [actif, resultats]);

  const ouvrir = (intervention) => {
    if (!intervention) return;
    onClose();
    onOuvrir(intervention);
  };

  if (!show) return null;

  const q = texte.trim();
  const plafondAtteint = resultats.length >= PLAFOND;

  return (
    <div className="modal-overlay">
      <div className="modal-content rdo" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Rechercher un dossier</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="rdo-saisie">
          <svg className="rdo-loupe" width="18" height="18" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" /><line x1="16.5" y1="16.5" x2="21" y2="21" />
          </svg>
          <input
            ref={champRef}
            value={texte}
            onChange={(e) => setTexte(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Nom, prénom, immatriculation, n° OR, téléphone, VIN…"
            aria-label="Rechercher un dossier dans tout le planning"
          />
          {texte && <button className="rdo-vider" onClick={() => { setTexte(''); champRef.current?.focus(); }} title="Effacer">✕</button>}
        </div>

        <div className="modal-body rdo-body" ref={listeRef}>
          {erreur && <div className="status-error">{erreur}</div>}

          {q.length < MINI ? (
            <div className="rdo-vide">
              <p>Cherche dans <strong>tout le planning</strong>, pas seulement la journée affichée.</p>
              <p className="rdo-discret">Deux caractères suffisent pour lancer la recherche.</p>
            </div>
          ) : chargement && resultats.length === 0 ? (
            <div className="rdo-vide">Recherche…</div>
          ) : resultats.length === 0 ? (
            <div className="rdo-vide">Aucun dossier ne correspond à « {q} ».</div>
          ) : (
            <>
              <div className="rdo-compteur">
                {resultats.length} dossier{resultats.length > 1 ? 's' : ''}
                {plafondAtteint && <span className="rdo-plafond"> — affinez votre recherche, seuls les {PLAFOND} plus récents sont affichés</span>}
              </div>
              {resultats.map((i, n) => {
                const fond = i.statut_atelier_couleur;
                const couleurTexte = fond ? (i.statut_atelier_text_color || getContrastColor(fond)) : undefined;
                return (
                  <button
                    type="button"
                    key={`${i.id}-${i._nbJours || 1}`}
                    className={`rdo-carte${n === actif ? ' is-active' : ''}`}
                    onClick={() => ouvrir(i)}
                    onMouseEnter={() => setActif(n)}
                    style={{ background: fond || 'var(--bg-surface)', color: couleurTexte }}
                  >
                    <div className="rdo-haut">
                      <span className="rdo-client">{i.client_nom || i.client || 'Client non renseigné'}{i.client_prenom ? ` ${i.client_prenom}` : ''}</span>
                      {i.immat && <span className="rdo-immat">{i.immat}</span>}
                    </div>
                    <div className="rdo-milieu">
                      <span>{[i.marque, i.modele].filter(Boolean).join(' ') || i.type || '—'}</span>
                      {i.numero_or && <span className="rdo-or">OR {i.numero_or}</span>}
                    </div>
                    {/* Ce que la grille ne montre pas, et qu'on cherche : quand. */}
                    <div className="rdo-bas">
                      <span className="rdo-quand">
                        {i.a_planifier ? 'À planifier' : `${formatDateFr(i.date)} · ${(i.heure_debut || '').slice(0, 5)}`}
                      </span>
                      {i._nbJours > 1 && (
                        <span className="rdo-jours" title="Rendez-vous Mécaplanning importé en un dossier par jour">
                          {i._nbJours} jours · du {formatDateFr(i._du)} au {formatDateFr(i._au)}
                        </span>
                      )}
                      {/* Date de planification = travail le plus lointain (compléments
                          inclus). Affichée quand elle dépasse la date de la ligne
                          principale, comme la colonne « Planification » des autres onglets. */}
                      {!i.a_planifier && i.date_planification && i.date_planification !== i.date && (
                        <span className="rdo-planif" title="Planification : travail le plus lointain du dossier (compléments inclus)">
                          → {formatDateFr(i.date_planification)}
                        </span>
                      )}
                      {i.mecanicien && <span className="rdo-meca">{i.mecanicien}</span>}
                      {i.statut_atelier_nom && <span className="rdo-statut">{i.statut_atelier_nom}</span>}
                      <span className="rdo-flags">
                        {i.is_complement && <CardFlagIcon type="complement" />}
                        {i.statut_pieces && <CardFlagIcon type="pieces" />}
                        {!i.client_tel && <CardFlagIcon type="tel" />}
                        {i.client_attend && <CardFlagIcon type="attend" />}
                        {i.a_courtoisie && <CardFlagIcon type="courtoisie" />}
                        {i.est_roole && <CardFlagIcon type="roole" />}
                        {i.a_demande && <CardFlagIcon type="demande" />}
                      </span>
                    </div>
                  </button>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
